/**
 * Utilitário de Máscara de Moeda Brasileira (BRL)
 * Formatação automática durante digitação com separadores de milhares e centavos
 */

/**
 * Aplica máscara de moeda brasileira em tempo real
 * Formato: 1.234.567,89
 * 
 * @param {string} value - Valor digitado pelo usuário
 * @returns {string} - Valor formatado com máscara
 * 
 * Exemplos:
 * "1" → "1"
 * "12" → "12"
 * "123" → "1,23" (assume centavos)
 * "1234" → "12,34"
 * "12345" → "123,45"
 * "123456" → "1.234,56"
 * "1234567" → "12.345,67"
 * "12345678" → "123.456,78"
 * "123456789" → "1.234.567,89"
 */
export function applyCurrencyMask(value) {
  if (!value) return '';
  
  // Remove tudo que não é número
  let numbers = value.replace(/\D/g, '');
  
  // Se vazio, retorna vazio
  if (numbers === '') return '';
  
  // Remove zeros à esquerda, mas mantém pelo menos um zero
  numbers = numbers.replace(/^0+/, '') || '0';
  
  // Converte para número para facilitar manipulação
  let numericValue = parseInt(numbers, 10);
  
  // Divide por 100 para obter o valor com centavos
  let valueInCents = numericValue / 100;
  
  // Formata com separadores de milhares e vírgula decimal
  return valueInCents.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

/**
 * Remove a máscara e retorna o valor numérico
 * 
 * @param {string} maskedValue - Valor com máscara (ex: "1.234,56")
 * @returns {number} - Valor numérico (ex: 1234.56)
 * 
 * Exemplos:
 * "1.234,56" → 1234.56
 * "123,45" → 123.45
 * "1,23" → 1.23
 * "0,50" → 0.50
 */
export function removeCurrencyMask(maskedValue) {
  if (!maskedValue) return 0;
  
  // Remove tudo que não é número
  const numbers = maskedValue.replace(/\D/g, '');
  
  if (numbers === '') return 0;
  
  // Converte para número e divide por 100 (centavos)
  return parseInt(numbers, 10) / 100;
}

/**
 * Formata um valor numérico para exibição com R$
 * 
 * @param {number} value - Valor numérico
 * @returns {string} - Valor formatado (ex: "R$ 1.234,56")
 */
export function formatCurrencyDisplay(value) {
  if (value === null || value === undefined || isNaN(value)) {
    return 'R$ 0,00';
  }
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}

/**
 * Formata valor para input (sem R$, apenas números formatados)
 * 
 * @param {number} value - Valor numérico
 * @returns {string} - Valor formatado (ex: "1.234,56")
 */
export function formatCurrencyForInput(value) {
  if (value === null || value === undefined) {
    return '';
  }
  
  // Converter para número se for string
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numericValue)) {
    return '';
  }
  
  return numericValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

/**
 * Hook para input de moeda com máscara automática
 * Retorna handlers para onChange e onBlur
 * 
 * @param {Function} onValueChange - Callback com valor numérico
 * @returns {Object} - { handleChange, handleBlur, formatValue }
 * 
 * Uso:
 * const { handleChange, handleBlur, formatValue } = useCurrencyInput(
 *   (numericValue) => setFieldValue('valor', numericValue)
 * );
 * 
 * <input
 *   value={formatValue(fieldValue)}
 *   onChange={handleChange}
 *   onBlur={handleBlur}
 * />
 */
export function useCurrencyInput(onValueChange) {
  const handleChange = (event) => {
    const inputValue = event.target.value;
    const maskedValue = applyCurrencyMask(inputValue);
    const numericValue = removeCurrencyMask(maskedValue);
    
    // Atualiza o input com máscara
    event.target.value = maskedValue;
    
    // Chama callback com valor numérico
    if (onValueChange) {
      onValueChange(numericValue);
    }
  };
  
  const handleBlur = (event) => {
    const inputValue = event.target.value;
    const numericValue = removeCurrencyMask(inputValue);
    const formattedValue = formatCurrencyForInput(numericValue);
    
    // Garante formatação completa no blur
    event.target.value = formattedValue;
    
    if (onValueChange) {
      onValueChange(numericValue);
    }
  };
  
  const formatValue = (value) => {
    return formatCurrencyForInput(value);
  };
  
  return {
    handleChange,
    handleBlur,
    formatValue
  };
}

/**
 * Valida se um valor monetário é válido
 * 
 * @param {number} value - Valor a validar
 * @param {Object} options - Opções de validação
 * @param {number} options.min - Valor mínimo permitido
 * @param {number} options.max - Valor máximo permitido
 * @returns {Object} - { valid: boolean, error: string }
 */
export function validateCurrencyValue(value, options = {}) {
  const { min = 0, max = Infinity } = options;
  
  if (value === null || value === undefined || isNaN(value)) {
    return { valid: false, error: 'Valor inválido' };
  }
  
  if (value < min) {
    return { 
      valid: false, 
      error: `Valor mínimo: ${formatCurrencyDisplay(min)}` 
    };
  }
  
  if (value > max) {
    return { 
      valid: false, 
      error: `Valor máximo: ${formatCurrencyDisplay(max)}` 
    };
  }
  
  return { valid: true, error: null };
}

/**
 * Componente de Input de Moeda (React)
 * Pode ser usado diretamente ou como referência
 */
export const CurrencyInputProps = {
  type: 'text',
  inputMode: 'numeric',
  placeholder: '0,00',
  maxLength: 20, // Suporta até 999.999.999.999.999,99
};
